import React from 'react';
import { View, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { FormSection, OptionCard } from '@/components';
import { IBulletinFormValues, BulletinFormFieldHandler } from '@/types/admin/bulletins';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { onColor } from '@/constants/shared/theme/onColor';

interface UrgencyOptionsSectionProps {
  formData: IBulletinFormValues;
  onFieldChange: BulletinFormFieldHandler;
}

export const UrgencyOptionsSection: React.FC<UrgencyOptionsSectionProps> = ({
  formData,
  onFieldChange,
}) => {
  const { redesign, semantic } = useAdminColors();

  return (
    <FormSection
      title="Urgency Features"
      subtitle="Optional settings to help bring more attention to the bulletin"
    >
      <View style={styles.container}>
        <View style={styles.optionsGrid}>
          <View style={styles.leftColumn}>
            <OptionCard
              title="Send Email"
              subtitle="Residents will receive email in addition to push notifications"
              value={formData.sendEmail}
              onPress={() => onFieldChange('sendEmail', !formData.sendEmail)}
              type="checkbox"
            />

            <OptionCard
              title="Highlight with Border"
              subtitle="Use a purple border to make the bulletin stand out visually"
              value={formData.highlight}
              onPress={() => onFieldChange('highlight', !formData.highlight)}
              type="checkbox"
            />
          </View>

          <View style={styles.rightColumn}>
            <OptionCard
              title="Pin to Homepage"
              subtitle="Place a bulletin shortcut in the residents' most visible spot"
              value={formData.pinHomepage}
              onPress={() => onFieldChange('pinHomepage', !formData.pinHomepage)}
              type="checkbox"
            />

            {formData.pinHomepage && (
              <View style={[styles.warningContainer, { backgroundColor: redesign.accent.orangeLight }]}>
                <View style={[styles.warningIcon, { backgroundColor: semantic.status.warning }]}>
                  <Text
                    style={[
                      styles.warningIconText,
                      { color: onColor(semantic.status.warning) },
                    ]}
                  >
                    !
                  </Text>
                </View>
                <Text style={[styles.warningText, { color: semantic.status.warning }]}>
                  This selection will override any other pinned bulletins.
                </Text>
              </View>
            )}
          </View>
        </View>
      </View>
    </FormSection>
  );
};

const styles = StyleSheet.create({
  container: {},
  optionsGrid: {
    flexDirection: 'row',
    gap: 16,
    alignItems: 'flex-start',
  },
  leftColumn: {
    flex: 1,
  },
  rightColumn: {
    flex: 1,
  },
  warningContainer: {
    borderRadius: 8,
    padding: 12,
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginTop: 8,
  },
  warningIcon: {
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8,
    marginTop: 1,
  },
  warningIconText: {
    // color applied inline via onColor(status.warning) — the badge fill is a
    // solid warning color, so the glyph ink must adapt to it per theme.
    fontSize: 12,
    fontWeight: '600',
  },
  warningText: {
    flex: 1,
    fontSize: 12,
    lineHeight: 16,
  },
});
